import { type ReactNode, useState } from "react";
import { useTranslations } from "next-intl";

import {
  LOGIN_WRAPPER_DEFAULTS,
  type TLoginType,
  type TLoginWrapperProps,
  type TLoginWrapperRememberMeProps,
} from "./LoginWrapper.config";

type TResolvedLoginWrapperRememberMeProps = TLoginWrapperRememberMeProps & {
  label: string;
};

const getNextLoginType = (loginType: TLoginType): TLoginType => (
  loginType === "loginCode" ? "password" : "loginCode"
);

const getToggleLabel = (
  nextLoginType: TLoginType,
  loginCodeLabel: ReactNode,
  passwordLabel: ReactNode,
): ReactNode => (
  nextLoginType === "loginCode" ? loginCodeLabel : passwordLabel
);

export const useLoginWrapper = (
  {
    title,
    subtitle,
    defaultLoginType = LOGIN_WRAPPER_DEFAULTS.defaultLoginType,
    loginType,
    onLoginTypeChange,
    toggle,
    rememberMe,
  }: Pick<
    TLoginWrapperProps,
    | "title"
    | "subtitle"
    | "defaultLoginType"
    | "loginType"
    | "onLoginTypeChange"
    | "toggle"
    | "rememberMe"
  >,
) => {
  const t = useTranslations("Libs.UI.Components.LoginWrapper");
  const [ internalLoginType, setInternalLoginType ] = useState<TLoginType>(defaultLoginType);
  const activeLoginType = loginType ?? internalLoginType;
  const nextLoginType = getNextLoginType(activeLoginType);
  const resolvedLoginCodeToggleLabel = toggle?.loginCodeLabel ?? t("loginCodeToggleLabel");
  const resolvedPasswordToggleLabel = toggle?.passwordLabel ?? t("passwordToggleLabel");
  const shouldRenderRememberMe = activeLoginType === "password" && rememberMe !== false;
  const resolvedRememberMe: TResolvedLoginWrapperRememberMeProps = rememberMe
    ? {
      ...rememberMe,
      label: rememberMe.label ?? t("rememberMeLabel"),
    }
    : {
      label: t("rememberMeLabel"),
    };
  const toggleLabel = getToggleLabel(
    nextLoginType,
    resolvedLoginCodeToggleLabel,
    resolvedPasswordToggleLabel,
  );

  const handleToggleClick = () => {
    if (!loginType) {
      setInternalLoginType(nextLoginType);
    }

    onLoginTypeChange?.(nextLoginType);
  };

  return {
    activeLoginType,
    handleToggleClick,
    resolvedRememberMe,
    shouldRenderRememberMe,
    subtitle: subtitle ?? t("subtitle"),
    title: title ?? t("title"),
    toggleLabel,
  };
};
